/**
 * InvoiceFiltersBar Component
 * Filter controls for invoices (desktop view)
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Filter, X, SlidersHorizontal } from 'lucide-react';
import type { InvoiceFilters } from '../types/invoice.types';
import type { InvoiceStatus } from '@/types/invoice';

// ============================================
// STATUS OPTIONS
// ============================================

const STATUS_OPTIONS: { value: InvoiceStatus; label: string; color: string }[] = [
  { value: 'DRAFT', label: 'Draft', color: 'bg-slate-100 text-slate-800' },
  { value: 'ISSUED', label: 'Issued', color: 'bg-blue-100 text-blue-800' },
  { value: 'PAID', label: 'Paid', color: 'bg-green-100 text-green-800' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid', color: 'bg-amber-100 text-amber-800' },
  { value: 'OVERDUE', label: 'Overdue', color: 'bg-red-100 text-red-800' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-slate-100 text-slate-800' },
];

// ============================================
// COMPONENT PROPS
// ============================================

export interface InvoiceFiltersBarProps {
  filters: InvoiceFilters;
  onSearchChange: (searchTerm: string) => void;
  onStatusToggle: (status: InvoiceStatus) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onClearFilters: () => void;
  onOpenMobileFilters?: () => void;
  activeFilterCount: number;
}

// ============================================
// COMPONENT
// ============================================

/**
 * InvoiceFiltersBar Component
 * Desktop filter controls with search, status filters, and date range
 *
 * @example
 * ```tsx
 * <InvoiceFiltersBar
 *   filters={filters}
 *   onSearchChange={setSearchTerm}
 *   onStatusToggle={toggleStatus}
 *   onDateRangeChange={setDateRange}
 *   onClearFilters={clearFilters}
 *   activeFilterCount={2}
 * />
 * ```
 */
export function InvoiceFiltersBar({
  filters,
  onSearchChange,
  onStatusToggle,
  onDateRangeChange,
  onClearFilters,
  onOpenMobileFilters,
  activeFilterCount,
}: InvoiceFiltersBarProps) {
  const [searchInput, setSearchInput] = useState(filters.searchTerm || '');
  const [dateFromInput, setDateFromInput] = useState(filters.dateFrom || '');
  const [dateToInput, setDateToInput] = useState(filters.dateTo || '');

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    onSearchChange(value);
  };

  const handleDateFromChange = (value: string) => {
    setDateFromInput(value);
    onDateRangeChange(value, dateToInput);
  };

  const handleDateToChange = (value: string) => {
    setDateToInput(value);
    onDateRangeChange(dateFromInput, value);
  };

  const selectedStatuses = filters.statuses || [];

  return (
    <div className="space-y-4">
      {/* Desktop View */}
      <div className="hidden md:flex items-center gap-3 flex-wrap">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[250px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by invoice number..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Date From Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600 whitespace-nowrap">From:</label>
          <Input
            type="date"
            value={dateFromInput}
            onChange={(e) => handleDateFromChange(e.target.value)}
            className="w-[150px]"
          />
        </div>

        {/* Date To Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600 whitespace-nowrap">To:</label>
          <Input
            type="date"
            value={dateToInput}
            onChange={(e) => handleDateToChange(e.target.value)}
            className="w-[150px]"
          />
        </div>

        {/* Status Filter Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="default">
              <Filter className="h-4 w-4 mr-2" />
              Status
              {selectedStatuses.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {selectedStatuses.length}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {STATUS_OPTIONS.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={selectedStatuses.includes(option.value)}
                onCheckedChange={() => onStatusToggle(option.value)}
              >
                <span className={`px-2 py-1 rounded text-xs font-medium ${option.color}`}>
                  {option.label}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Clear Filters Button */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="default"
            onClick={onClearFilters}
            className="text-slate-600 hover:text-slate-900"
          >
            <X className="h-4 w-4 mr-2" />
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Mobile View */}
      <div className="md:hidden space-y-3">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search invoices..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Mobile Filter Button */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="default"
            onClick={onOpenMobileFilters}
            className="flex-1"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount}
              </Badge>
            )}
          </Button>

          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClearFilters}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Active Filter Tags */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-600">Active filters:</span>
          {selectedStatuses.map((status) => (
            <Badge
              key={status}
              variant="secondary"
              className="cursor-pointer hover:bg-slate-200"
              onClick={() => onStatusToggle(status)}
            >
              {STATUS_OPTIONS.find(opt => opt.value === status)?.label}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          ))}
          {dateFromInput && (
            <Badge
              variant="secondary"
              className="cursor-pointer hover:bg-slate-200"
              onClick={() => handleDateFromChange('')}
            >
              From: {new Date(dateFromInput).toLocaleDateString()}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          )}
          {dateToInput && (
            <Badge
              variant="secondary"
              className="cursor-pointer hover:bg-slate-200"
              onClick={() => handleDateToChange('')}
            >
              To: {new Date(dateToInput).toLocaleDateString()}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export default InvoiceFiltersBar;
